import Component from '@glimmer/component';
import { PhArrowRight } from 'ember-phosphor-icons';
import PipelineStage from './pipeline-stage';
import type { PipelineStageColor, PipelineStageSize } from './pipeline-stage';

interface StageData {
  value: string | number;
  label: string;
  color?: PipelineStageColor;
}

interface PipelineFlowSignature {
  Args: {
    /** Array of stages to display */
    stages: StageData[];
    /** Size variant for stages - sm, md, or lg */
    size?: PipelineStageSize;
    /** Whether to use flex-1 for equal width stages */
    equalWidth?: boolean;
    /** Whether stages should wrap on small screens */
    wrap?: boolean;
  };
  Element: HTMLDivElement;
}

/**
 * PipelineFlow - A horizontal flow visualization with stages and arrows
 *
 * Renders a series of PipelineStage components connected by arrows,
 * commonly used to show leadership pipeline or process stages.
 *
 * @example
 * ```gts
 * import PipelineFlow from 'tio-ui/components/pipeline-flow';
 *
 * <PipelineFlow
 *   @stages={{array
 *     (hash value=45 label="Emerging" color="violet")
 *     (hash value=28 label="Developing" color="teal")
 *     (hash value=12 label="Ready Now" color="green")
 *   }}
 * />
 * ```
 */
export default class PipelineFlow extends Component<PipelineFlowSignature> {
  get wrapClass() {
    return this.args.wrap ? 'flex-wrap' : '';
  }

  get gapClass() {
    return this.args.size === 'lg' ? 'gap-3' : 'gap-4';
  }

  <template>
    <div
      data-test-pipeline-flow
      class="tio-row-between {{this.gapClass}} {{this.wrapClass}}"
      ...attributes
    >
      {{#each @stages as |stage index|}}
        {{#if (gt index 0)}}
          <PhArrowRight
            data-test-pipeline-flow-connector
            @size={{24}}
            class="tio-fg-disabled shrink-0"
          />
        {{/if}}
        <PipelineStage
          @value={{stage.value}}
          @label={{stage.label}}
          @color={{stage.color}}
          @size={{if @size @size "md"}}
          class={{if @equalWidth "flex-1"}}
        />
      {{/each}}
    </div>
  </template>
}

export type { PipelineFlowSignature, StageData };
